// P1-020: Agent collection grid — neon industrial codex
import { useState, useMemo, useCallback, useRef } from 'react';
import { AGENT_CONFIGS } from '../data/agent-configs';
import type { AgentConfig } from '../data/agent-configs';
import { useAgentStore } from '../stores/agentStore';
import { rarityLabel, typeLabel, agentPortrait, typeBgColor, typeAbbr, typeLabelCN } from '../utils/agent-ui-helpers';

const PAGE_SIZE = 5;

export function CollectionPanel() {
  const ownedAgents = useAgentStore((s) => s.ownedAgents);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);

  const ownedConfigIds = useMemo(() => {
    const ids = new Set<string>();
    for (const agent of ownedAgents) {
      ids.add(agent.configId);
    }
    return ids;
  }, [ownedAgents]);

  const portraitUrlMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of ownedAgents) {
      if (agent.generatedPortraitUrl && !map.has(agent.configId)) {
        map.set(agent.configId, agent.generatedPortraitUrl);
      }
    }
    return map;
  }, [ownedAgents]);

  // Sort: owned first, then locked
  const sortedConfigs = useMemo(() => {
    return [...AGENT_CONFIGS].sort((a, b) => {
      const aOwned = ownedConfigIds.has(a.id) ? 0 : 1;
      const bOwned = ownedConfigIds.has(b.id) ? 0 : 1;
      return aOwned - bOwned;
    });
  }, [ownedConfigIds]);

  const maxOffset = Math.max(0, sortedConfigs.length - PAGE_SIZE);
  const visibleConfigs = sortedConfigs.slice(offset, offset + PAGE_SIZE);

  // Find best owned agent for the selected config (highest level)
  const selectedConfig = useMemo(
    () => (selectedId ? AGENT_CONFIGS.find((c) => c.id === selectedId) : null),
    [selectedId],
  );
  const bestOwned = useMemo(() => {
    if (!selectedId) return null;
    const matches = ownedAgents
      .filter((a) => a.configId === selectedId)
      .sort((a, b) => b.level - a.level);
    return matches[0] ?? null;
  }, [selectedId, ownedAgents]);

  const accumulatedDelta = useRef(0);
  const SCROLL_THRESHOLD = 80;

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    accumulatedDelta.current += e.deltaY;
    if (accumulatedDelta.current >= SCROLL_THRESHOLD) {
      accumulatedDelta.current = 0;
      setOffset((prev) => Math.min(prev + 1, maxOffset));
    } else if (accumulatedDelta.current <= -SCROLL_THRESHOLD) {
      accumulatedDelta.current = 0;
      setOffset((prev) => Math.max(prev - 1, 0));
    }
  }, [maxOffset]);

  /** Render detail stats from either OwnedAgent or base AgentConfig */
  const renderDetail = (config: AgentConfig) => {
    const owned = bestOwned;
    const name = owned ? owned.name : '???';
    const level = owned ? owned.level : 1;
    const atk = owned ? owned.attack : config.baseATK;
    const def = owned ? owned.defense : config.baseDEF;
    const hp = owned ? owned.hp : config.baseHP;
    const crit = owned ? owned.critRate : 5 + config.rarity * 2;
    const compute = owned ? owned.computeCost : config.baseComputeCost;
    const skill = owned ? owned.activeSkill : config.activeSkill;

    return (
      <>
        <div className="agent-terminal__detail-name">{name}</div>
        <div className="agent-terminal__detail-type">
          {typeAbbr(config.type)} · {typeLabelCN(config.type)}
        </div>
        <div className="agent-terminal__detail-rarity">
          {'★'.repeat(config.rarity)} · LV.{level}
        </div>

        <div className="agent-terminal__detail-section">ATTRIBUTES</div>
        <div className="agent-terminal__detail-stats">
          <div className="agent-terminal__detail-stat">
            <span className="agent-terminal__detail-stat-label">ATK</span>
            <span className="agent-terminal__detail-stat-value" style={{ color: 'var(--color-danger)' }}>{atk}</span>
          </div>
          <div className="agent-terminal__detail-stat">
            <span className="agent-terminal__detail-stat-label">DEF</span>
            <span className="agent-terminal__detail-stat-value" style={{ color: 'var(--color-info)' }}>{def}</span>
          </div>
          <div className="agent-terminal__detail-stat">
            <span className="agent-terminal__detail-stat-label">HP</span>
            <span className="agent-terminal__detail-stat-value">{hp}</span>
          </div>
          <div className="agent-terminal__detail-stat">
            <span className="agent-terminal__detail-stat-label">CRIT</span>
            <span className="agent-terminal__detail-stat-value">{crit}%</span>
          </div>
          <div className="agent-terminal__detail-stat">
            <span className="agent-terminal__detail-stat-label">COMPUTE</span>
            <span className="agent-terminal__detail-stat-value">{compute}</span>
          </div>
        </div>

        <div className="agent-terminal__detail-section">SKILLS</div>
        <div className="agent-terminal__detail-skill">
          <span
            className="agent-terminal__detail-skill-dot"
            style={{ background: typeBgColor(config.type) }}
          />
          <div className="agent-terminal__detail-skill-info">
            <div className="agent-terminal__detail-skill-name">{skill.name}</div>
            <div className="agent-terminal__detail-skill-desc">{skill.description}</div>
            <div className="agent-terminal__detail-skill-cd">CD: {skill.cooldown}</div>
          </div>
        </div>

        {bestOwned?.wishText && (
          <>
            <div className="agent-terminal__detail-section">WISH</div>
            <div className="collection-panel__wish-tag">
              "{bestOwned.wishText}"
            </div>
          </>
        )}
      </>
    );
  };

  return (
    <div className="collection-panel">
      <div className="collection-panel__body">
        <div className="collection-panel__grid" onWheel={handleWheel}>
          {visibleConfigs.map((config) => {
            const owned = ownedConfigIds.has(config.id);
            const isSelected = selectedId === config.id;
            return (
              <div
                key={config.id}
                className={`collection-panel__card ${isSelected ? 'collection-panel__card--selected' : ''} ${!owned ? 'collection-panel__card--locked' : ''}`}
                onClick={() => setSelectedId(config.id)}
              >
                {/* Portrait card */}
                <div
                  className={`collection-panel__card-portrait ${isSelected ? 'collection-panel__card-portrait--active' : ''}`}
                >
                  <img
                    className="collection-panel__card-portrait-img"
                    src={agentPortrait(config.id, portraitUrlMap.get(config.id))}
                    alt={config.name}
                  />
                  {/* Info overlay at bottom */}
                  <div className="collection-panel__card-info">
                    <span className={`collection-panel__card-name ${isSelected ? 'collection-panel__card-name--active' : ''}`}>
                      {owned ? config.name.split('-')[0] : '???'}
                    </span>
                    <span className={`collection-panel__card-meta ${isSelected ? 'collection-panel__card-meta--active' : ''}`}>
                      {'★'.repeat(config.rarity)} · {rarityLabel(config.rarity)} · {typeLabel(config.type)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Detail panel — reuses agent-terminal__detail styling */}
        {selectedConfig && (
          <div className="agent-terminal__detail collection-panel__detail">
            {renderDetail(selectedConfig)}
          </div>
        )}
      </div>

      {/* Pager — scroll wheel to shift by 1 agent */}
      {maxOffset > 0 && (
        <div className="collection-panel__pager">
          <button
            className="collection-panel__pager-btn"
            disabled={offset <= 0}
            onClick={() => setOffset((p) => Math.max(p - 1, 0))}
          >
            ‹
          </button>
          <div className="collection-panel__pager-track">
            <div
              className="collection-panel__pager-thumb"
              style={{ left: `${(offset / maxOffset) * 100}%` }}
            />
          </div>
          <span className="collection-panel__pager-label">
            {offset + 1}–{offset + PAGE_SIZE} / {sortedConfigs.length}
          </span>
          <button
            className="collection-panel__pager-btn"
            disabled={offset >= maxOffset}
            onClick={() => setOffset((p) => Math.min(p + 1, maxOffset))}
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
